import type { TFunction } from 'i18next'
import * as yup from 'yup'
import { isCompleteTrPhone } from '@/lib/phone'
import type { CalismaTuru, PersonelDurum } from '../types'
const TCKN_RE = /^(\*{3,}\d{2}|\d{11})$/
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^\d{2}:\d{2}$/
const MONEY_RE = /^\d+(\.\d{1,2})?$/

function requiredString(t: TFunction, min = 2) {
  return yup
    .string()
    .trim()
    .required(t('hr.personel.form.errors.required'))
    .min(min, t('hr.personel.form.errors.min', { min }))
}

function optionalString() {
  return yup.string().trim().default('')
}

function emailField(t: TFunction, required: boolean) {
  if (required) {
    return yup
      .string()
      .trim()
      .required(t('hr.personel.form.errors.required'))
      .email(t('hr.personel.form.errors.email'))
  }
  return yup
    .string()
    .trim()
    .default('')
    .test('email', t('hr.personel.form.errors.email'), (v) => !v || yup.string().email().isValidSync(v))
}

function phoneField(t: TFunction, required: boolean) {
  if (required) {
    return yup
      .string()
      .trim()
      .required(t('hr.personel.form.errors.required'))
      .test('phone', t('hr.personel.form.errors.phone'), (v) => !!v && isCompleteTrPhone(v))
  }
  return yup
    .string()
    .trim()
    .default('')
    .test('phone', t('hr.personel.form.errors.phone'), (v) => !v || isCompleteTrPhone(v))
}

function isoDateField(t: TFunction) {
  return yup
    .string()
    .trim()
    .required(t('hr.personel.form.errors.required'))
    .matches(ISO_DATE_RE, t('hr.personel.form.errors.date'))
}

export const CALISMA_TURU_VALUES = ['tam_zamanli', 'hibrit', 'yari_zamanli'] as const satisfies readonly CalismaTuru[]
export const DURUM_VALUES = ['aktif', 'izinli', 'deneme'] as const satisfies readonly PersonelDurum[]
export const CINSIYET_VALUES = ['erkek', 'kadin', 'diger'] as const
export const MEDENI_VALUES = ['bekar', 'evli', 'bosanmis', 'dul'] as const
export const SIGORTA_VALUES = ['sgk', 'bagkur', 'emekli'] as const
export const ISTIHDAM_VALUES = ['tam_zamanli', 'yari_zamanli', 'stajyer', 'sozlesmeli'] as const
export const EGITIM_DURUMU_VALUES = ['lise', 'onlisans', 'lisans', 'yuksek_lisans', 'doktora'] as const

export type PersonelFormMode = 'create' | 'edit'

function baseFields(t: TFunction) {
  return {
    ad: requiredString(t),
    dogum: isoDateField(t),
    tckn: yup
      .string()
      .trim()
      .required(t('hr.personel.form.errors.required'))
      .matches(TCKN_RE, t('hr.personel.form.errors.tckn')),
    cinsiyet: yup
      .string()
      .oneOf([...CINSIYET_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    medeni: yup
      .string()
      .oneOf([...MEDENI_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    uyruk: requiredString(t),
    foto_adi: optionalString(),
    /** Seçilen profil fotoğrafı; kayıttan sonra yüklenir. */
    foto_dosya: yup.mixed<File>().nullable().default(null),

    /** Sicil numarası BE tarafından otomatik verilir (salt okunur). */
    sicil: optionalString(),
    durum: yup
      .string()
      .oneOf([...DURUM_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    departman_id: requiredString(t, 1),
    pozisyon: requiredString(t),
    sube: requiredString(t),
    istihdam: yup
      .string()
      .oneOf([...ISTIHDAM_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    sigorta: yup
      .string()
      .oneOf([...SIGORTA_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    ortak: yup.boolean().default(false),
    ortaklik_payi: yup
      .string()
      .trim()
      .default('')
      .test('share', t('hr.personel.form.errors.share'), (v) => !v || (Number(v) > 0 && Number(v) <= 100)),
    // Prim ödenmeyen emeklide emeklilik tarihi zorunlu (bordroda prim kesilmemesinin dayanağı).
    emeklilik_tarihi: yup
      .string()
      .trim()
      .default('')
      .test('iso', t('hr.personel.form.errors.date'), (v) => !v || ISO_DATE_RE.test(v))
      .test('required-when-retired', t('hr.personel.form.errors.retiredAtRequired'), function (v) {
        return this.parent.sigorta !== 'emekli' || !!v
      }),
    emeklilik_notu: yup.string().trim().max(1000).default(''),
    calisma_turu: yup
      .string()
      .oneOf([...CALISMA_TURU_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    ise_giris: isoDateField(t),
    calisma_baslangic: yup
      .string()
      .trim()
      .required(t('hr.personel.form.errors.required'))
      .matches(TIME_RE, t('hr.personel.form.errors.time')),
    calisma_bitis: yup
      .string()
      .trim()
      .required(t('hr.personel.form.errors.required'))
      .matches(TIME_RE, t('hr.personel.form.errors.time')),
    /** Yönetici personel id; en üst kademe için boş kalabilir. */
    yonetici: optionalString(),

    /** Ücretin geçerlilik başlangıcı; boşsa yeni kayıtta işe giriş, güncellemede bugün. Geçmiş tarih girilebilir. */
    maas_gecerlilik: yup
      .string()
      .trim()
      .default('')
      .test('iso', t('hr.personel.form.errors.date'), (v) => !v || ISO_DATE_RE.test(v)),
    brut_maas: yup
      .string()
      .trim()
      .required(t('hr.personel.form.errors.required'))
      .matches(MONEY_RE, t('hr.personel.form.errors.money')),

    egitim_durumu: yup
      .string()
      .oneOf([...EGITIM_DURUMU_VALUES], t('hr.personel.form.errors.option'))
      .required(t('hr.personel.form.errors.required')),
    universite_id: optionalString(),
    mezun_bolum: optionalString(),

    email: emailField(t, true),
    telefon: phoneField(t, true),
    is_tel: phoneField(t, false),

    il_id: requiredString(t, 1),
    ilce_id: requiredString(t, 1),
    adres: requiredString(t),

    acil_ad: requiredString(t),
    acil_yakinlik: requiredString(t),
    acil_telefon: phoneField(t, true),
    acil_email: emailField(t, false),
    acil_adres: optionalString(),

    panel_erisim: yup.boolean().default(false),
    firma_email: optionalString(),
    role_ids: yup.array().of(yup.string().required()).default([]),
    mailbox_assign: yup.boolean().default(false),
    mailbox_provider_id: optionalString(),
    mailbox_secret: optionalString(),
    mailbox_signature: optionalString(),
    sifre: optionalString(),
    sifre_tekrar: optionalString(),
    sifre_sifirla: yup.boolean().default(false),

    belgeler: yup
      .array()
      .of(
        yup.object({
          tip: yup.string().required(),
          dosya_adi: yup.string().default(''),
          dosya: yup.mixed<File>().nullable().default(null),
        }),
      )
      .default([]),
  }
}

function withPanelRules<T extends yup.AnyObject>(schema: yup.ObjectSchema<T>, t: TFunction, mode: PersonelFormMode) {
  return schema
    .test('panel-email', t('hr.personel.form.errors.firmaEmail'), function (values) {
      if (!values?.panel_erisim) return true
      const email = String(values.firma_email ?? '').trim()
      if (!email) {
        return this.createError({ path: 'firma_email', message: t('hr.personel.form.errors.required') })
      }
      if (!yup.string().email().isValidSync(email)) {
        return this.createError({ path: 'firma_email', message: t('hr.personel.form.errors.email') })
      }
      return true
    })
    .test('panel-roles', t('hr.personel.form.rolesRequired'), function (values) {
      // Create'te zorunlu; edit'te yeni erişim açılırken PersonelFormPage kontrol eder
      if (mode !== 'create' || !values?.panel_erisim) return true
      const roles = (values.role_ids ?? []) as string[]
      if (roles.length === 0) {
        return this.createError({ path: 'role_ids', message: t('hr.personel.form.rolesRequired') })
      }
      return true
    })
    .test('panel-mailbox', t('hr.personel.form.mailboxSecretRequired'), function (values) {
      if (!values?.panel_erisim || !values?.mailbox_assign) return true
      if (!String(values.mailbox_provider_id ?? '').trim()) {
        return this.createError({ path: 'mailbox_provider_id', message: t('hr.personel.form.mailboxProviderRequired') })
      }
      if (!String(values.mailbox_secret ?? '').trim()) {
        return this.createError({ path: 'mailbox_secret', message: t('hr.personel.form.mailboxSecretRequired') })
      }
      return true
    })
}

export function createPersonelFormSchema(t: TFunction, mode: PersonelFormMode) {
  const schema = yup.object(baseFields(t))
  return withPanelRules(schema, t, mode)
}

export type PersonelFormValues = yup.InferType<ReturnType<typeof createPersonelFormSchema>>

/** @deprecated use createPersonelFormSchema('create') */
export function createPersonelAddSchema(t: TFunction) {
  return createPersonelFormSchema(t, 'create')
}

/** @deprecated use createPersonelFormSchema('edit') */
export function createPersonelEditSchema(t: TFunction) {
  return createPersonelFormSchema(t, 'edit')
}

export type PersonelAddFormValues = PersonelFormValues
export type PersonelEditFormValues = PersonelFormValues

/** Belge satırları BE'deki tür listesinden (`/hr/document-kinds`) oluşur; başlangıçta boştur. */
export function emptyBelgeler(): PersonelFormValues['belgeler'] {
  return []
}

export const PERSONEL_FORM_DEFAULTS: PersonelFormValues = {
  ad: '',
  dogum: '',
  tckn: '',
  cinsiyet: 'erkek',
  medeni: 'bekar',
  uyruk: 'T.C.',
  foto_adi: '',
  foto_dosya: null,
  sicil: '',
  durum: 'aktif',
  departman_id: '',
  pozisyon: '',
  sube: '',
  istihdam: 'tam_zamanli',
  sigorta: 'sgk',
  ortak: false,
  ortaklik_payi: '',
  emeklilik_tarihi: '',
  emeklilik_notu: '',
  calisma_turu: 'tam_zamanli',
  ise_giris: '',
  calisma_baslangic: '09:00',
  calisma_bitis: '18:00',
  yonetici: '',
  brut_maas: '',
  maas_gecerlilik: '',
  egitim_durumu: 'lisans',
  universite_id: '',
  mezun_bolum: '',
  email: '',
  telefon: '',
  is_tel: '',
  il_id: '',
  ilce_id: '',
  adres: '',
  acil_ad: '',
  acil_yakinlik: '',
  acil_telefon: '',
  acil_email: '',
  acil_adres: '',
  panel_erisim: false,
  firma_email: '',
  role_ids: [] as string[],
  mailbox_assign: false,
  mailbox_provider_id: '',
  mailbox_secret: '',
  mailbox_signature: '',
  sifre: '',
  sifre_tekrar: '',
  sifre_sifirla: false,
  belgeler: emptyBelgeler(),
}

export const PERSONEL_ADD_DEFAULTS = PERSONEL_FORM_DEFAULTS
